import React, { useCallback } from 'react';
import { LuArrowRight, LuInfo, LuSave, LuSquarePen, LuX } from 'react-icons/lu';
import { Link, useBlocker, useLocation, useNavigate } from 'react-router-dom';

import { cn } from 'utils/cn';

import { BaseCardProps } from './BaseCard';
import { ConfirmModal } from './ConfirmModal';
import { useEditableCardGroup } from './EditableCardGroupContext';
import { Spinner } from './Spinner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export interface EditableCardProps extends BaseCardProps {
    editing?: boolean;
    onEdit?: () => void;
    onCancel?: () => void;
    onSave?: () => void;
    saveDisabled?: boolean;
    saving?: boolean;
    saveTooltipMessage?: string;
    editDisabled?: boolean;
    editGroup?: string;
    editCardId?: string;
    hasChanges?: boolean;
}

const useOptionalEditableCardGroup = () => {
    try {
        return useEditableCardGroup();
    } catch {
        return null;
    }
};

export const EditableCard = ({
    className,
    title,
    titleLink,
    subTitle,
    icon,
    headerClassName,
    navigateUrl,
    children,
    loading = false,
    menu,
    editing = false,
    onEdit,
    onCancel,
    onSave,
    saveDisabled = false,
    saving = false,
    saveTooltipMessage,
    editDisabled = false,
    editGroup,
    editCardId,
    hasChanges = false,
}: EditableCardProps) => {
    const editGroupContext = useOptionalEditableCardGroup();

    // Use edit group context if available and editGroup is provided, otherwise fall back to props
    const cardId = editCardId || (typeof title === 'string' ? title : 'untitled');
    const shouldUseEditGroup = editGroup && editGroupContext;
    const isCurrentlyEditing = shouldUseEditGroup ? editGroupContext.isEditing(editGroup, cardId) : editing;
    const isEditDisabled = shouldUseEditGroup ? editGroupContext.isEditingDisabled(editGroup, cardId) : editDisabled;

    if (shouldUseEditGroup && !editCardId) {
        throw new Error('Card id must be provided for EditableCard with an editGroup');
    }

    // Block navigation when editing with unsaved changes
    const blocker = useBlocker(useCallback(() => isCurrentlyEditing && hasChanges, [isCurrentlyEditing, hasChanges]));

    const handleEditClick = () => {
        if (shouldUseEditGroup && editGroupContext) {
            editGroupContext.setEditingCard(editGroup, cardId);
        }
        onEdit?.();
    };

    const handleCancelClick = () => {
        if (shouldUseEditGroup && editGroupContext) {
            editGroupContext.setEditingCard(editGroup, null);
        }
        onCancel?.();
    };

    const handleSaveClick = () => {
        onSave?.();
        // Note: Don't automatically clear editing state on save - let the component handle it
    };

    const navigate = useNavigate();
    const { pathname } = useLocation();

    const Icon = icon || LuInfo;
    const IconComponent = () => {
        if (icon) {
            return <Icon className="h-5 w-5 text-black" />;
        }
        return <></>;
    };

    const MaybeWrapWithLink = ({ children: linkChildren, link }: { children: React.ReactNode; link?: string }) => {
        if (link) {
            return <Link to={link}>{linkChildren}</Link>;
        }
        return <>{linkChildren}</>;
    };

    const headerCn = cn('group', isCurrentlyEditing ? 'bg-[#d1b08e]' : 'bg-gray-300', headerClassName);
    return (
        <>
            <Card className={cn(className, 'overflow-hidden rounded-t-xl')}>
                <CardHeader className={headerCn}>
                    <div className="flex items-center gap-2">
                        <IconComponent />
                        <MaybeWrapWithLink link={titleLink}>
                            <CardTitle className="text-lg font-semibold text-black">
                                <div>{title}</div>
                                {subTitle && <div className="text-sm font-normal text-gray-700">{subTitle}</div>}
                            </CardTitle>
                        </MaybeWrapWithLink>

                        <div className="ml-auto flex items-center gap-2">
                            {menu && <div>{menu}</div>}
                            {!isCurrentlyEditing ? (
                                <button
                                    onClick={handleEditClick}
                                    disabled={isEditDisabled}
                                    className="cursor-pointer rounded p-1 text-gray-600 hover:bg-gray-300 hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <LuSquarePen size={16} />
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={handleCancelClick}
                                        className="cursor-pointer rounded p-1 text-gray-600 hover:bg-gray-300 hover:text-black"
                                    >
                                        <LuX size={16} />
                                    </button>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    onClick={handleSaveClick}
                                                    disabled={saveDisabled || saving}
                                                    className="cursor-pointer rounded p-1 text-gray-600 hover:bg-gray-300 hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    {saving ? (
                                                        <div className="pointer-events-none">
                                                            <Spinner size="sm" />
                                                        </div>
                                                    ) : (
                                                        <LuSave size={16} />
                                                    )}
                                                </button>
                                            </TooltipTrigger>
                                            {(saveDisabled || saving) && saveTooltipMessage && (
                                                <TooltipContent>
                                                    <p>{saveTooltipMessage}</p>
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    </TooltipProvider>
                                </>
                            )}
                            {navigateUrl && (
                                <div
                                    className="cursor-pointer opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                                    onClick={() =>
                                        navigate(pathname.split('/').slice(0, -1).join('/') + '/' + navigateUrl)
                                    }
                                >
                                    <LuArrowRight />
                                </div>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-8">
                            <Spinner showText />
                        </div>
                    ) : (
                        children
                    )}
                </CardContent>
            </Card>

            <ConfirmModal
                open={blocker.state === 'blocked'}
                onOpenChange={(open) => {
                    if (!open) blocker.reset?.();
                }}
                title="You have unsaved changes"
                description="Are you sure you want to leave? Your unsaved changes will be lost."
                confirmText="Leave"
                cancelText="Stay"
                confirmVariant="destructive"
                onConfirm={() => blocker.proceed?.()}
                onCancel={() => blocker.reset?.()}
            />
        </>
    );
};
